import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Building2, Download, MoreVertical, Eye, Settings, UserX, UserCheck } from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Checkbox } from '../components/ui/checkbox'
import { DataTable } from '../components/ui/DataTable'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu'
import { ColumnDef } from '@tanstack/react-table'
import { Customer } from '../types/partner'

type StatusFilter = 'all' | 'active' | 'inactive'
type TierFilter = 'all' | 'Standard' | 'Professional' | 'Enterprise' | 'Premium'

const normalizeStatus = (status?: string | null) => (status || 'Active').toLowerCase()

export default function Customers() {
  const { managedCustomers, isLoading } = useAuth()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [tierFilter, setTierFilter] = useState<TierFilter>('all')
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([])

  // Table columns definition (Duralux-style)
  const columns = useMemo<ColumnDef<Customer>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40,
      },
      {
        accessorKey: 'customerName',
        header: 'Customer',
        cell: ({ row }) => {
          const customer = row.original
          return (
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="font-medium text-sm text-slate-900">{customer.customerName}</div>
                <div className="text-xs text-slate-500">{customer.customerId.substring(0, 24)}...</div>
              </div>
            </div>
          )
        },
        size: 300,
      },
      {
        accessorKey: 'subscriptionTier',
        header: 'Tier',
        cell: ({ row }) => {
          const tier = row.original.subscriptionTier
          return (
            <Badge variant={tier === 'Enterprise' ? 'success' : tier === 'Professional' ? 'default' : 'secondary'} size="sm">
              {tier}
            </Badge>
          )
        },
        size: 140,
      },
      {
        accessorKey: 'licenseCount',
        header: 'Licenses',
        cell: ({ row }) => {
          return <span className="text-sm text-slate-700">{row.original.licenseCount}</span>
        },
        size: 100,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const customer = row.original
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 transition-colors">
                  <span className={`h-2 w-2 rounded-full ${customer.status === 'Active' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                  <span className="text-sm text-slate-700">{customer.status}</span>
                  <svg className="h-3 w-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); /* handle status change */ }}>
                  <span className="h-2 w-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); /* handle status change */ }}>
                  <span className="h-2 w-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
        size: 130,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const customer = row.original
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <button className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors">
                  <MoreVertical className="h-4 w-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/customers/${customer.customerId}`)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/customers/${customer.customerId}`)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Manage Customer
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className={customer.status === 'Active' ? 'text-rose-600' : 'text-emerald-600'}>
                  {customer.status === 'Active' ? (
                    <>
                      <UserX className="h-4 w-4 mr-2" />
                      Suspend Customer
                    </>
                  ) : (
                    <>
                      <UserCheck className="h-4 w-4 mr-2" />
                      Activate Customer
                    </>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
        size: 80,
      },
    ],
    [navigate]
  )

  const filteredCustomers = useMemo(() => {
    return managedCustomers
      .filter(customer => {
        if (statusFilter === 'all') return true
        const normalized = normalizeStatus(customer.status)
        return normalized === statusFilter
      })
      .filter(customer => {
        if (tierFilter === 'all') return true
        return customer.subscriptionTier === tierFilter
      })
  }, [managedCustomers, statusFilter, tierFilter])

  const handleExportCustomers = () => {
    // TODO: Implement actual export logic
  }

  const handleBulkAction = (action: 'activate' | 'suspend') => {
    // TODO: Implement actual bulk action logic
  }

  if (isLoading) {
    return (
      <div className="flex-1 bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-3 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500 font-medium">Loading customers...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Content - Table View */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-[1800px] mx-auto">
          <Card>
            <CardContent className="p-0">
              {/* Filters + Actions Bar */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <select
                      value={tierFilter}
                      onChange={(e) => setTierFilter(e.target.value as TierFilter)}
                      className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="all">All Tiers</option>
                      <option value="Standard">Standard</option>
                      <option value="Professional">Professional</option>
                      <option value="Enterprise">Enterprise</option>
                      <option value="Premium">Premium</option>
                    </select>
                    {(statusFilter !== 'all' || tierFilter !== 'all') && (
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setTierFilter('all');
                        }}
                        className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedCustomers.length > 0 && (
                      <>
                        <span className="text-sm text-slate-600 mr-2">{selectedCustomers.length} selected</span>
                        <Button variant="outline" size="sm" onClick={() => handleBulkAction('activate')}>
                          Activate
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleBulkAction('suspend')}>
                          Suspend
                        </Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" onClick={handleExportCustomers}>
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Data Table */}
              <div className="p-6">
                <DataTable
                  columns={columns}
                  data={filteredCustomers}
                  searchKey="customerName"
                  searchPlaceholder="Search by name or ID..."
                  onRowClick={(customer) => navigate(`/customers/${customer.customerId}`)}
                  enableRowSelection={true}
                  onSelectionChange={setSelectedCustomers}
                  pageSize={10}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
